import { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { supabase } from '../lib/supabase';
import { getSubscriptionStatus, formatCurrency } from '../lib/subscriptionUtils';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/cn';
import type { CustomerSubscription } from './SubscriptionFormModal';

export function SubscriptionRenewalAlert() {
    const [expiring, setExpiring] = useState<CustomerSubscription[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            const { data } = await supabase
                .from('customer_subscriptions')
                .select('*, customers(company_name)')
                .eq('status', 'active')
                .order('next_renewal_date', { ascending: true });

            if (data) {
                const urgent = (data as CustomerSubscription[]).filter(
                    s => getSubscriptionStatus(s.next_renewal_date) !== 'ok'
                );
                setExpiring(urgent);
            }
            setLoading(false);
        };
        fetch();
    }, []);

    if (loading || expiring.length === 0) return null;

    const expiredCount = expiring.filter(s => getSubscriptionStatus(s.next_renewal_date) === 'expired').length;
    const warningCount = expiring.filter(s => getSubscriptionStatus(s.next_renewal_date) === 'warning').length;

    return (
        <Card className={cn(
            'border-l-4',
            expiredCount > 0 ? 'border-l-red-500 bg-red-50' : 'border-l-orange-500 bg-orange-50'
        )}>
            <CardContent className="p-4 md:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <AlertTriangle className={cn('w-5 h-5 mt-0.5 flex-shrink-0', expiredCount > 0 ? 'text-red-500' : 'text-orange-500')} />
                        <div>
                            <h3 className={cn('font-semibold text-sm', expiredCount > 0 ? 'text-red-800' : 'text-orange-800')}>
                                {expiredCount > 0 && `${expiredCount} subscription scadut${expiredCount === 1 ? 'a' : 'e'}`}
                                {expiredCount > 0 && warningCount > 0 && ' · '}
                                {warningCount > 0 && `${warningCount} in scadenza entro 30 giorni`}
                            </h3>
                            <div className="mt-2 space-y-1">
                                {expiring.slice(0, 3).map(sub => {
                                    const st = getSubscriptionStatus(sub.next_renewal_date);
                                    return (
                                        <div key={sub.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-700">
                                            <CreditCard className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                            <span className="font-medium break-words">{(sub.customers as any)?.company_name}</span>
                                            <span className="text-gray-500">—</span>
                                            <span>{formatCurrency(sub.monthly_amount)}/mese</span>
                                            <span className={cn(
                                                'px-1.5 py-0.5 rounded text-[10px] font-medium',
                                                st === 'expired' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                                            )}>
                                                {format(parseISO(sub.next_renewal_date), 'd MMM yyyy', { locale: it })}
                                            </span>
                                        </div>
                                    );
                                })}
                                {expiring.length > 3 && (
                                    <p className="text-xs text-gray-500">e altri {expiring.length - 3}...</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/subscriptions"
                        className={cn(
                            'flex items-center gap-1 text-xs font-medium whitespace-nowrap',
                            expiredCount > 0 ? 'text-red-600 hover:text-red-700' : 'text-orange-600 hover:text-orange-700'
                        )}
                    >
                        Gestisci <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
}
